/**
 * @file: useMentionCompose.ts
 *
 * `@mention` compose state: the picker overlay, the chips the user has
 * inserted (ranges resolved from the final text at send time), and a handle
 * to the composer so a picked member is spliced in at the `@` anchor.
 *
 * Opening the picker freezes the keyboard inset so blurring for the picker
 * doesn't move the composer; a pick re-focuses (keyboard rises into the
 * frozen inset) and schedules an unfreeze once settled; a dismiss without
 * picking unfreezes and collapses immediately.
 */

import { ChatComposerHandle } from '@euler/app/flows/chat/components/composer/ChatComposer';
import {
  ActiveMention,
  MentionCandidate,
} from '@euler/app/flows/chat/utils/mentions';
import { useCallback, useRef, useState } from 'react';
import { KeyboardInset } from './useKeyboardInset';

export function useMentionCompose(keyboard: KeyboardInset) {
  const composerRef = useRef<ChatComposerHandle>(null);
  const [mentionPickerOpen, setMentionPickerOpen] = useState(false);
  const [mentions, setMentions] = useState<ActiveMention[]>([]);

  const openMentionPicker = useCallback(() => {
    keyboard.freeze();
    setMentionPickerOpen(true);
  }, [keyboard]);

  // Dismiss WITHOUT selecting: the keyboard won't return, so unfreeze and
  // collapse the inset (list reflows to full height).
  const dismissMentionPicker = useCallback(() => {
    keyboard.unfreeze();
    setMentionPickerOpen(false);
  }, [keyboard]);

  const onPickMention = useCallback(
    (candidate: MentionCandidate) => {
      // Insert the chip; the composer re-focuses itself once the picker
      // modal has dismissed (~320ms) and the keyboard rises into the frozen
      // inset — no movement. Unfreeze AFTER it has settled.
      setMentionPickerOpen(false);
      setMentions(prev => [
        ...prev,
        { target: candidate.target, displayName: candidate.displayName },
      ]);
      composerRef.current?.insertMention('@' + candidate.displayName);
      keyboard.scheduleResync();
    },
    [keyboard],
  );

  return {
    composerRef,
    mentionPickerOpen,
    mentions,
    setMentions,
    openMentionPicker,
    dismissMentionPicker,
    onPickMention,
  };
}
